import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    Grid,
    LinearProgress,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark, faLocationDot, faPlugCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from 'ApplicationContext';
import SettingsPageHeader from './Settings/SettingsPageHeader';
import { GeocodingSettingsDto, GeocodingTestResultDto, UpdateGeocodingSettingsRequest } from 'apiClient/Api';

/** Auswahl für die Ergebnissprache — deckt die Sprachen der Oberfläche ab. */
const LANGUAGES = [
    { value: 'de', label: 'Deutsch' },
    { value: 'en', label: 'Englisch' },
    { value: 'fr', label: 'Französisch' },
    { value: 'it', label: 'Italienisch' },
    { value: 'es', label: 'Spanisch' },
];

/** Häufige Länder-Einschränkungen als Schnellauswahl. */
const COUNTRY_PRESETS = [
    { value: 'de', label: 'nur Deutschland' },
    { value: 'de,at,ch', label: 'DACH' },
    { value: '', label: 'weltweit' },
];

/** Leerer Einstellungssatz als Startpunkt für das Formular. */
const emptyForm: UpdateGeocodingSettingsRequest = {
    enabled: false,
    baseUrl: 'https://nominatim.openstreetmap.org',
    userAgent: '',
    contactEmail: '',
    language: 'de',
    countryCodes: 'de',
    maxResults: 5,
    timeoutSeconds: 10,
    minRequestIntervalMs: 1100,
    cacheMinutes: 60,
};

const toForm = (dto: GeocodingSettingsDto): UpdateGeocodingSettingsRequest => ({
    enabled: dto.enabled ?? false,
    baseUrl: dto.baseUrl ?? '',
    userAgent: dto.userAgent ?? '',
    contactEmail: dto.contactEmail ?? '',
    language: dto.language ?? 'de',
    countryCodes: dto.countryCodes ?? '',
    maxResults: dto.maxResults ?? 5,
    timeoutSeconds: dto.timeoutSeconds ?? 10,
    minRequestIntervalMs: dto.minRequestIntervalMs ?? 1100,
    cacheMinutes: dto.cacheMinutes ?? 60,
});

/** Erkennt die öffentliche OSM-Instanz — für sie gilt das strenge Rate-Limit. */
const isPublicInstance = (baseUrl: string): boolean =>
    baseUrl.toLowerCase().includes('nominatim.openstreetmap.org');

/**
 * Einrichtung der Geocoding-Anbindung (Nominatim/OpenStreetMap). Daraus werden
 * die Koordinaten der Anlagen-Standorte ermittelt.
 *
 * Alle Werte greifen ohne Server-Neustart — sie liegen in `ServerSettings` und
 * überlagern die Startwerte aus `appsettings.json`.
 */
export const GeocodingSettings: React.FC = () => {
    const { api, apiError } = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();

    const [settings, setSettings] = useState<GeocodingSettingsDto | null>(null);
    const [form, setForm] = useState<UpdateGeocodingSettingsRequest>(emptyForm);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<GeocodingTestResultDto | null>(null);

    const reload = useCallback(() => {
        setLoading(true);
        api.geocoding
            .geocodingGetSettings()
            .then((r) => {
                setSettings(r.data);
                setForm(toForm(r.data));
            })
            .catch(apiError)
            .finally(() => setLoading(false));
    }, [api, apiError]);

    useEffect(() => reload(), [reload]);

    const patch = (changes: Partial<UpdateGeocodingSettingsRequest>) =>
        setForm((prev) => ({ ...prev, ...changes }));

    const save = async (): Promise<void> => {
        setSaving(true);
        try {
            const response = await api.geocoding.geocodingSaveSettings(form);
            setSettings(response.data);
            setForm(toForm(response.data));
            setTestResult(null);
            enqueueSnackbar('Einstellungen gespeichert — sie greifen ohne Neustart.', { variant: 'success' });
        } catch (e: any) {
            // Der Server antwortet bei Validierungsfehlern mit Klartext.
            const detail = e?.response?.status === 400 ? e?.response?.data : null;
            if (typeof detail === 'string' && detail.length > 0) {
                enqueueSnackbar(detail, { variant: 'error' });
            } else {
                apiError(e);
            }
        } finally {
            setSaving(false);
        }
    };

    const runTest = async (): Promise<void> => {
        setTesting(true);
        setTestResult(null);
        try {
            const response = await api.geocoding.geocodingTestConnection();
            setTestResult(response.data);
        } catch (e) {
            apiError(e);
        } finally {
            setTesting(false);
        }
    };

    const active = (settings?.enabled ?? false) && (settings?.isComplete ?? false);
    const publicInstance = isPublicInstance(form.baseUrl ?? '');
    const rateLimitTooLow = publicInstance && (form.minRequestIntervalMs ?? 0) < 1000;

    return (
        <Grid size={12}>
            <SettingsPageHeader sectionKey="geocoding" />
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            <Grid container spacing={2}>
                <Grid size={12}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}
                        >
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                <FontAwesomeIcon icon={active ? faCircleCheck : faCircleXmark} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {active ? 'Geocoding aktiv' : 'Geocoding inaktiv'}
                                </Typography>
                                {settings?.enabled && !settings?.isComplete && (
                                    <Chip size="small" color="warning" label="unvollständig konfiguriert" />
                                )}
                                {settings?.baseUrl && (
                                    <Chip size="small" variant="outlined" label={settings.baseUrl} />
                                )}
                                {settings?.countryCodes
                                    ? <Chip size="small" variant="outlined" label={`Länder: ${settings.countryCodes}`} />
                                    : <Chip size="small" variant="outlined" label="weltweit" />}
                            </Stack>
                            <Button
                                variant="outlined"
                                onClick={runTest}
                                disabled={testing || loading}
                                startIcon={<FontAwesomeIcon icon={faPlugCircleCheck} />}
                            >
                                {testing ? 'Teste …' : 'Verbindung testen'}
                            </Button>
                        </Stack>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                            Wandelt die Adresse einer Anlage in Koordinaten um. Ist der Dienst abgeschaltet,
                            lässt sich eine Adresse weiterhin eintragen — sie bleibt dann ohne Koordinaten.
                        </Typography>

                        {testResult && (
                            <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                                <AlertTitle>
                                    {testResult.success ? 'Verbindung erfolgreich' : 'Verbindung fehlgeschlagen'}
                                </AlertTitle>
                                {testResult.message}
                                {testResult.firstResult && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Treffer: {testResult.firstResult.displayName} (
                                        {testResult.firstResult.latitude?.toFixed(6)},{' '}
                                        {testResult.firstResult.longitude?.toFixed(6)})
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    Dauer: {testResult.elapsedMilliseconds} ms
                                </Typography>
                            </Alert>
                        )}
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2.5 }}>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <FontAwesomeIcon icon={faLocationDot} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Anbindung
                                </Typography>
                            </Stack>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.enabled ?? false}
                                        onChange={(e) => patch({ enabled: e.target.checked })}
                                    />
                                }
                                label="Geocoding aktiv"
                            />

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Basis-URL"
                                        value={form.baseUrl ?? ''}
                                        onChange={(e) => patch({ baseUrl: e.target.value })}
                                        helperText="Öffentliche Instanz oder eigener Server. Ohne abschließenden Slash."
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="User-Agent"
                                        value={form.userAgent ?? ''}
                                        onChange={(e) => patch({ userAgent: e.target.value })}
                                        helperText="Pflicht bei der öffentlichen Instanz — muss den Betreiber erkennbar machen."
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Kontakt-E-Mail"
                                        value={form.contactEmail ?? ''}
                                        onChange={(e) => patch({ contactEmail: e.target.value })}
                                        helperText="Wird mitgeschickt, damit der Betreiber bei Problemen anfragt statt zu sperren."
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 3 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Sprache der Ergebnisse"
                                        value={form.language ?? 'de'}
                                        onChange={(e) => patch({ language: e.target.value })}
                                    >
                                        {LANGUAGES.map((l) => (
                                            <MenuItem key={l.value} value={l.value}>
                                                {l.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid size={{ xs: 12, md: 3 }}>
                                    <TextField
                                        fullWidth
                                        label="Länder (ISO-Codes)"
                                        value={form.countryCodes ?? ''}
                                        onChange={(e) => patch({ countryCodes: e.target.value })}
                                        helperText="Komma-Liste, z.B. de,at,ch. Leer = weltweit."
                                    />
                                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                                        {COUNTRY_PRESETS.map((preset) => (
                                            <Chip
                                                key={preset.label}
                                                size="small"
                                                variant={(form.countryCodes ?? '') === preset.value ? 'filled' : 'outlined'}
                                                label={preset.label}
                                                onClick={() => patch({ countryCodes: preset.value })}
                                            />
                                        ))}
                                    </Stack>
                                </Grid>
                            </Grid>

                            <Divider />

                            <Typography variant="subtitle2">Grenzwerte</Typography>

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 3 }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Max. Treffer"
                                        value={form.maxResults ?? 5}
                                        onChange={(e) => patch({ maxResults: Number(e.target.value) })}
                                        helperText="1–50, Vorschläge pro Suche."
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 3 }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Timeout (s)"
                                        value={form.timeoutSeconds ?? 10}
                                        onChange={(e) => patch({ timeoutSeconds: Number(e.target.value) })}
                                        helperText="1–120."
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 3 }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Mindestabstand (ms)"
                                        value={form.minRequestIntervalMs ?? 1100}
                                        onChange={(e) => patch({ minRequestIntervalMs: Number(e.target.value) })}
                                        error={rateLimitTooLow}
                                        helperText={
                                            rateLimitTooLow
                                                ? 'Die öffentliche Instanz erlaubt max. 1 Anfrage/Sekunde — mindestens 1000 setzen.'
                                                : 'Abstand zwischen zwei Anfragen an den Dienst.'
                                        }
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 3 }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Cache (Minuten)"
                                        value={form.cacheMinutes ?? 60}
                                        onChange={(e) => patch({ cacheMinutes: Number(e.target.value) })}
                                        helperText="0–1440. 0 schaltet den Cache ab."
                                    />
                                </Grid>
                            </Grid>

                            {publicInstance && (
                                <Alert severity="info">
                                    Sie nutzen die öffentliche OpenStreetMap-Instanz. Deren Nutzungsbedingungen
                                    erlauben maximal eine Anfrage pro Sekunde für den gesamten Server und verlangen
                                    einen aussagekräftigen User-Agent. Bei spürbarer Wartezeit lohnt sich eine
                                    eigene Nominatim-Instanz — dann nur die Basis-URL umstellen.
                                </Alert>
                            )}

                            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                                <Button onClick={reload} disabled={saving || loading}>
                                    Verwerfen
                                </Button>
                                <Button variant="contained" onClick={save} disabled={saving || loading}>
                                    {saving ? 'Speichere …' : 'Speichern'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default GeocodingSettings;
